import { apiFetch } from "../utils/apiFetch";
import { useEffect, useRef, useState } from "react";
import { FiUser, FiLoader, FiLogOut, FiSettings, FiCreditCard } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router";
import ThemeToggle from "./ThemeToggle";
import { ownerPlan } from "../utils/communityEntitlements.mjs";
import { clearDisplayUser } from "../utils/authStorage";

const AccountMenu = ({ align = "right", sidebar = false, avatar = true, bottom = false }) => {
  const { userInfo, setUserId, setUserInfo } = useAuth();
  const navigate = useNavigate();
  const plan = { default: "Free", plus: "Plus", pro: "Pro" }[ownerPlan(userInfo?.subscriptionInfo)];
  const [signingOut, setSigningOut] = useState(false);
  const [open, setOpen] = useState(false);
  const [failedAvatar, setFailedAvatar] = useState(null);
  const picture = userInfo?.avatar && failedAvatar !== userInfo.avatar
    ? <img src={userInfo.avatar} alt="" onError={() => setFailedAvatar(userInfo.avatar)} className="h-7 w-7 rounded-full object-cover" /> : null;
  const initials = (userInfo?.username || "Account").trim().split(/\s+/).slice(0, 2).map(word => Array.from(word)[0]).join("").toUpperCase();
  const [error, setError] = useState("");
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const logout = async () => {
    if (signingOut) return;
    setSigningOut(true);
    setError("");
    try {
      const response = await apiFetch(`${process.env.API_URL}/api/auth/logout`, { method: "POST" });
      if (!response.ok) throw new Error("Could not sign out. Please try again.");
      clearDisplayUser();
      setUserId(null);
      setUserInfo(null);
      navigate("/", { replace: true });
    } catch (error) { setError(error.message); }
    finally { setSigningOut(false); }
  };

  useEffect(() => {
    if (!open) return;
    const outside = (event) => { if (!containerRef.current?.contains(event.target)) setOpen(false); };
    const escape = (event) => { if (event.key === "Escape") { setOpen(false); buttonRef.current?.focus(); } };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);


  return (
    <div ref={containerRef} className="relative shrink-0 text-gray-950" onClick={(event) => event.stopPropagation()}>
      <button ref={buttonRef} type="button" aria-label={bottom ? "Profile" : "Account menu"} aria-expanded={open}
        onClick={() => setOpen(!open)} title="Account"
        className={bottom ? "group-bottom-action" : avatar ? "group-profile-avatar" : sidebar ? "flex min-h-14 w-full items-center gap-2 rounded-md p-2 text-left hover:bg-gray-100" : "flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white hover:bg-gray-50"}>
        {bottom ? <><span className="group-profile-avatar">{picture || <span aria-hidden="true">{initials}</span>}</span><span>Profile</span></> : avatar ? picture || <span aria-hidden="true">{(userInfo?.username || "Account").trim().split(/\s+/).slice(0, 2).map(word => Array.from(word)[0]).join("").toUpperCase()}</span> :
        sidebar ? <>{picture}<span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold" title={userInfo?.username}>{userInfo?.username || "Account"}</span><span className="block truncate text-xs text-gray-500" title={userInfo?.email}>{userInfo?.email}</span></span><FiSettings aria-hidden="true" className="h-5 w-5 shrink-0" /></> : picture || <FiUser aria-hidden="true" className="h-5 w-5" />}
      </button>
      {open && <div className={`${bottom ? "absolute bottom-full right-0 mb-2 max-h-[65dvh] w-64 overflow-y-auto" : sidebar ? "absolute bottom-full left-0 mb-2 max-h-[60dvh] w-full overflow-y-auto" : align === "left" ? "relative w-full" : "absolute right-0 top-full w-64"} z-40 mt-2 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-100 bg-white p-3 text-sm shadow-lg`}>
        <div className="mb-3 flex items-center justify-between gap-3"><div className="min-w-0"><p className="break-words font-semibold">{userInfo?.username || "Account"}</p><p className="break-words text-xs text-gray-500">{userInfo?.email}</p></div><span aria-label={`${plan} plan`} className="shrink-0 rounded-md bg-[var(--app-raised)] px-2 py-1 text-xs font-semibold text-[var(--app-text)]">{plan}</span></div>
        <div className="mb-3 flex items-center justify-between border-y border-gray-100 py-2"><span className="font-medium">Appearance</span><ThemeToggle /></div>
        <button type="button" onClick={() => { setOpen(false); navigate("/settings"); }} className="flex min-h-10 w-full items-center gap-2 rounded-md px-2 text-left hover:bg-gray-50"><FiSettings aria-hidden="true" />Settings</button>
        <button type="button" onClick={() => { setOpen(false); navigate("/settings?setting=subscription"); }} className="flex min-h-10 w-full items-center gap-2 rounded-md px-2 text-left hover:bg-gray-50"><FiCreditCard aria-hidden="true" />Plans &amp; billing</button>
        {error && <p role="alert" className="my-2 text-xs text-red-700">{error}</p>}
        <button type="button" onClick={logout} disabled={signingOut} aria-busy={signingOut} className="mt-2 flex min-h-10 w-full items-center gap-2 border-t border-gray-100 px-2 text-left hover:bg-gray-50 disabled:cursor-wait">
          {signingOut ? <FiLoader aria-hidden="true" className="h-4 w-4 animate-spin" /> : <FiLogOut aria-hidden="true" className="h-4 w-4" />}Sign out
        </button>
      </div>}

    </div>
  );
};

export default AccountMenu;
